import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import { TriggerWidgetStore } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/store/triggerWidgetStore'
import { TriggerStep1 } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/ui/Steps/TriggerStep1'
import { TriggerStep2 } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/ui/Steps/TriggerStep2'
import { TriggerStep3 } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/ui/Steps/TriggerStep3'
import { TriggerStep4 } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/ui/Steps/TriggerStep4'

export const TriggerWidget = observer(() => {
  const { triggerSectionStore, chatStore } = useUnifiedAgentContext()
  const [store] = useState(() => new TriggerWidgetStore(triggerSectionStore, chatStore))

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  useWidgetKeyboard({
    onNavigate:
      store.currentStepKey === 'howTriggered' || store.currentStepKey === 'reEnroll'
        ? (dir) => store.moveFocus(dir)
        : undefined,
    onEnter: store.handleSelect,
    onNext: !store.isLastStep && !store.isNextDisabled ? store.handleNext : undefined,
    onSubmit: store.isSubmitDisabled ? undefined : store.handleSubmit,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const getHintItems = (): string[] => {
    if (store.currentStepKey === 'howTriggered')
      return getLastStepHintMessage(['↑↓ to navigate', 'Enter to select'])
    if (store.currentStepKey === 'reEnroll') return getLastStepHintMessage(['↑↓ to navigate'])
    return getLastStepHintMessage([])
  }

  const renderContent = () => {
    switch (store.currentStepKey) {
      case 'howTriggered':
        return (
          <TriggerStep1
            runAutomatically={store.localRunAutomatically}
            onSelect={store.selectAt}
            enrollByWebhookStore={store.enrollByWebhookStore}
            focusedIndex={store.safeIndex}
          />
        )
      case 'whichInbox':
        return <TriggerStep2 sendFromStore={store.localSendFromStore} />
      case 'contactsEnroll':
        return (
          <TriggerStep3
            enrollmentFilters={store.localEnrollmentFilters}
            onEdit={store.editLocalEnrollment}
          />
        )
      case 'reEnroll':
        return <TriggerStep4 focusedIndex={store.safeIndex} onSelect={store.setFocusedIndex} />
      default:
        return null
    }
  }

  return (
    <>
      <WidgetCard header={store.stepHeader}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            store.isLastStep ? (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
                disabled={store.isSubmitDisabled}
              />
            ) : (
              <IconButton
                icon={'arrowRight'}
                ariaLabel={'WidgetNext'}
                color={'primary'}
                onClick={store.handleNext}
                disabled={store.isNextDisabled}
              />
            )
          }
        />
      </WidgetCard>
      <WidgetHint items={getHintItems()} />
    </>
  )
})
